import {
    Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Badge,
} from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt, faUser, faTruck, faClock, faFileAlt, faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { needsDarkText } from '@s20/common/util/style';
import { appendTimeZone } from '@s20/common/util/date';
import { statusColors } from './constants';

// Helper functions extracted outside component as they don't use local state
const formatAddress = (jobData) => {
    const parts = [
        jobData.streetAddress,
        jobData.city,
        jobData.state,
        jobData.zipCode,
    ].filter((part) => part && part.trim());

    return parts.join(', ');
};

const CalendarJobDetails = ({ job, isOpen, onClose }) => {
    if (!job) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
        >
            <ModalHeader toggle={onClose}>
                Job Details: {job.jobNumber}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                                <h5>{job.description || 'No description available'}</h5>
                                {job.projectName && (
                                    <p className="text-muted">Project: {job.projectName}</p>
                                )}
                            </div>
                            <div>
                                {(job.jobId || job.id) && (
                                    <Link
                                        to={`/job/${job.jobId || job.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary"
                                        title="Open job in new window"
                                    >
                                        <FontAwesomeIcon
                                            icon={faExternalLinkAlt}
                                            className="mr-1"
                                        />
                                        View Full Details
                                    </Link>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <div>
                            <FontAwesomeIcon
                                icon={faClock}
                                className="mr-2"
                            />
                            <strong>Start Time:</strong>
                            {' '}
                            {appendTimeZone(moment.parseZone(job.startTime).format('MMM DD, YYYY h:mm A'), job.startTime, job.timeZone)}
                        </div>
                        {job.workOrderNumber && (
                            <div className="mt-2">
                                <FontAwesomeIcon
                                    icon={faFileAlt}
                                    className="mr-2"
                                />
                                <strong>Work Order:</strong>
                                {' '}
                                {job.workOrderNumber}
                            </div>
                        )}
                    </Col>
                    <Col md={6}>
                        <div>
                            <FontAwesomeIcon
                                icon={faUser}
                                className="mr-2"
                            />
                            <strong>Staff:</strong>
                            {' '}
                            {job.staffCount || 0}
                            {job.requestedStaffCount > 0 && job.requestedStaffCount !== job.staffCount && (
                                <span className="text-muted"> (of {job.requestedStaffCount} requested)</span>
                            )}
                        </div>
                        <div className="mt-2">
                            <FontAwesomeIcon
                                icon={faTruck}
                                className="mr-2"
                            />
                            <strong>Vehicles:</strong>
                            {' '}
                            {job.vehicleCount || 0}
                            {job.requestedVehicleCount > 0 && job.requestedVehicleCount !== job.vehicleCount && (
                                <span className="text-muted"> (of {job.requestedVehicleCount} requested)</span>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={12}>
                        <h6>Customer Information</h6>
                        <div>
                            <strong>Customer:</strong>
                            {' '}
                            {job.customerName}
                        </div>
                        {formatAddress(job) && (
                            <div className="mt-1">
                                <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="mr-2"
                                />
                                <strong>Address:</strong>
                                {' '}
                                {formatAddress(job)}
                            </div>
                        )}
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <h6>Job Information</h6>
                        {job.jobType && (
                            <div>
                                <strong>Type:</strong>
                                {' '}
                                <span
                                    className="job-type-badge"
                                    style={{
                                        backgroundColor: job.jobTypeColor || '#9ca3af',
                                        color: needsDarkText(job.jobTypeColor || '#9ca3af') ? '#000000' : '#ffffff',
                                    }}
                                >
                                    {job.jobType}
                                </span>
                            </div>
                        )}
                        <div className="mt-2">
                            <strong>Status:</strong>
                            {' '}
                            <span
                                className="job-status-badge"
                                style={{
                                    backgroundColor: statusColors[job.status || job.jobStatus] || '#777',
                                }}
                            >
                                {job.status || job.jobStatus || 'Unfilled'}
                            </span>
                        </div>
                    </Col>
                    <Col md={6}>
                        <h6>Location Information</h6>
                        <div>
                            <strong>Dispatch:</strong>
                            {' '}
                            {job.dispatchLocationName}
                        </div>
                        {job.meetingLocationName && (
                            <div className="mt-1">
                                <strong>Meeting:</strong>
                                {' '}
                                {job.meetingLocationName}
                            </div>
                        )}
                        {job.jobLocationName && (
                            <div className="mt-1">
                                <strong>Job Site:</strong>
                                {' '}
                                {job.jobLocationName}
                            </div>
                        )}
                    </Col>
                </Row>

                {/* Show assigned employee names */}
                {job.assignedEmployeeNames && job.assignedEmployeeNames.length > 0 && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <h6>Assigned Staff</h6>
                            <div className="assignment-list">
                                {job.assignedEmployeeNames.map((name, index) => (
                                    <Badge
                                        key={index}
                                        color="primary"
                                        className="mr-2 mb-1"
                                    >
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="mr-1"
                                        />
                                        {name}
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Show assigned equipment names */}
                {job.assignedEquipmentNames && job.assignedEquipmentNames.length > 0 && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <h6>Assigned Equipment</h6>
                            <div className="assignment-list">
                                {job.assignedEquipmentNames.map((name, index) => (
                                    <Badge
                                        key={index}
                                        color="info"
                                        className="mr-2 mb-1"
                                    >
                                        <FontAwesomeIcon
                                            icon={faTruck}
                                            className="mr-1"
                                        />
                                        {name}
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Show detailed assignments if available */}
                {job.assignments && job.assignments.length > 0 && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <h6>Assignment Details</h6>
                            <div className="assignment-list">
                                {job.assignments.map((assignment, index) => (
                                    <div
                                        key={assignment.assignmentId || index}
                                        className="mb-2"
                                    >
                                        <div className="d-flex align-items-center">
                                            <span className="mr-2">
                                                {assignment.employeeName || assignment.equipmentName}
                                            </span>
                                            {assignment.isCrewLead && (
                                                <Badge
                                                    color="warning"
                                                    className="mr-2"
                                                >Lead</Badge>
                                            )}
                                            {assignment.confirmed && <Badge color="success">Confirmed</Badge>}
                                            {assignment.rejected && <Badge color="danger">Rejected</Badge>}
                                            {!assignment.confirmed && !assignment.rejected && <Badge color="secondary">Pending</Badge>}
                                            {assignment.equipmentName && (
                                                <span className="ml-2 text-muted">
                                                    ({assignment.equipmentType}: {assignment.equipmentName})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {job.notes && (
                    <Row>
                        <Col md={12}>
                            <h6>Notes</h6>
                            <div className="border rounded p-2 bg-light">
                                {job.notes}
                            </div>
                        </Col>
                    </Row>
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CalendarJobDetails;
